import classNames from 'classnames';
import { type ReactNode, type RefObject, useContext, useMemo, useState } from 'react';

import PageContext from '@/Providers/PageContextProvider/PageContext';
import { getBrandingLogoUrl, isLightLogoInDark, lightLogoBackdropClass } from '@/shared/utils/logo';
import { layoutClassNames } from '@/utils/consts';

import { ColumnFrameContext, useIsWideDesktop } from '../ColumnFrame';

import BrandPanel from './BrandPanel';
import CustomContent from './CustomContent';

type Props = {
  // eslint-disable-next-line @typescript-eslint/ban-types -- React 19's useRef(null) yields RefObject<T | null>
  readonly mainRef: RefObject<HTMLElement | null>;
  readonly children: ReactNode;
};

/**
 * Desktop: the form column beside the tenant's brand panel. From 1024px the column is framed:
 * a header row (logo, back / skip) above the page and the legal links below it, all on the
 * page's start edge. Narrower, the panel and frame drop out and the page renders as it does alone.
 */
const SplitLayout = ({ mainRef, children }: Props) => {
  const { experienceSettings, theme } = useContext(PageContext);
  const isWide = useIsWideDesktop();
  const [nav, setNav] = useState<HTMLElement>();
  const [footer, setFooter] = useState<HTMLElement>();
  const slots = useMemo(() => (isWide ? { nav, footer } : {}), [isWide, nav, footer]);

  const logo =
    experienceSettings &&
    getBrandingLogoUrl({
      theme,
      branding: experienceSettings.branding,
      isDarkModeEnabled: experienceSettings.color.isDarkModeEnabled,
    });

  const main = (
    <main
      ref={mainRef}
      className={classNames(
        'flex w-full flex-col',
        !isWide && 'my-auto max-w-100',
        layoutClassNames.mainContent
      )}
    >
      {children}
    </main>
  );

  return (
    <ColumnFrameContext.Provider value={slots}>
      <div className="absolute inset-0 flex bg-background">
        {/* The form column is the scroller, so a tall page scrolls beside the panel, not behind it.
            `overscroll-y-none`: pull-to-refresh would otherwise reload the page mid-OTP. */}
        <div className="min-w-0 flex-1 overflow-y-auto overscroll-y-none">
          <div
            className={classNames(
              // Centred with the child's `my-auto`, not `justify-center`: a tall page would
              // otherwise overflow above the scroll origin and its top become unreachable.
              'flex min-h-full flex-col items-center',
              isWide ? 'px-16 py-12' : 'px-8 py-12',
              layoutClassNames.pageContainer
            )}
          >
            <CustomContent className={layoutClassNames.customContent} />
            {isWide ? (
              // Header, page and legal links are one group, anchored near the top so Back and the
              // logo hold their place from step to step instead of moving with each page's height.
              <div className="mt-[min(10dvh,6rem)] mb-auto flex w-full max-w-100 flex-col">
                <div
                  className={classNames(
                    'column-header relative mb-12 flex h-10 w-full shrink-0 items-center',
                    layoutClassNames.columnHeader
                  )}
                >
                  {logo && (
                    <img
                      className={classNames(
                        'h-8 w-auto max-w-40 object-contain',
                        isLightLogoInDark(theme, experienceSettings.branding, logo) &&
                          lightLogoBackdropClass
                      )}
                      alt="app logo"
                      src={logo}
                    />
                  )}
                  <div
                    ref={(node) => {
                      setNav(node ?? undefined);
                    }}
                    className="column-header__nav"
                  />
                </div>
                {main}
                <div
                  ref={(node) => {
                    setFooter(node ?? undefined);
                  }}
                  className={classNames(
                    'column-footer mt-8 w-full shrink-0 empty:hidden',
                    'animate-enter motion-reduce:animate-none',
                    layoutClassNames.columnFooter
                  )}
                />
              </div>
            ) : (
              main
            )}
          </div>
        </div>
        <BrandPanel />
      </div>
    </ColumnFrameContext.Provider>
  );
};

export default SplitLayout;
